import { useState, useEffect } from 'react'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { customersApi, personsApi, type Customer, type Person, type CustomerContact } from '@/lib/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogBody,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Plus,
  Loader2,
  Building2,
  User,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  Globe,
  MoreVertical,
  Pencil,
  Trash2,
  Users,
  UserPlus,
  X,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type CustomerType = 'prospect' | 'active' | 'churned' | 'partner'

const customerTypeColors: Record<CustomerType, string> = {
  prospect: 'bg-warning/10 text-warning border-warning/20',
  active: 'bg-business/10 text-business border-business/20',
  churned: 'bg-error/10 text-error border-error/20',
  partner: 'bg-solution/10 text-solution border-solution/20',
}

export function CRMPage() {
  const { currentProject } = useProjectStore()
  const { currentAccount } = useAuthStore()
  const [activeTab, setActiveTab] = useState('customers')
  const [customers, setCustomers] = useState<Customer[]>([])
  const [persons, setPersons] = useState<Person[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Dialog state
  const [showCustomerDialog, setShowCustomerDialog] = useState(false)
  const [showPersonDialog, setShowPersonDialog] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null)
  const [editingPerson, setEditingPerson] = useState<Person | null>(null)
  const [deleteCustomerId, setDeleteCustomerId] = useState<string | null>(null)
  const [deletePersonId, setDeletePersonId] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Contact management state
  const [showContactsDialog, setShowContactsDialog] = useState(false)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [customerContacts, setCustomerContacts] = useState<CustomerContact[]>([])
  const [isLoadingContacts, setIsLoadingContacts] = useState(false)
  const [showAddContactDialog, setShowAddContactDialog] = useState(false)

  // Form state
  const [customerForm, setCustomerForm] = useState({
    customerName: '',
    customerType: '' as CustomerType | '',
    billingAddress: '',
    city: '',
    country: '',
    industry: '',
    website: '',
    notes: '',
  })

  const [personForm, setPersonForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobTitle: '',
    notes: '',
  })

  useEffect(() => {
    if (currentAccount && currentProject) {
      loadData()
    }
  }, [currentAccount, currentProject])

  const loadData = async () => {
    if (!currentAccount || !currentProject) return
    setIsLoading(true)
    setError(null)
    try {
      const [customersResult, personsResult] = await Promise.all([
        customersApi.list(currentAccount.id, currentProject.id),
        personsApi.list(currentAccount.id, currentProject.id),
      ])
      setCustomers(customersResult)
      setPersons(personsResult)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateCustomer = () => {
    setEditingCustomer(null)
    setCustomerForm({
      customerName: '',
      customerType: '',
      billingAddress: '',
      city: '',
      country: '',
      industry: '',
      website: '',
      notes: '',
    })
    setShowCustomerDialog(true)
  }

  const handleEditCustomer = (customer: Customer) => {
    setEditingCustomer(customer)
    setCustomerForm({
      customerName: customer.customerName,
      customerType: (customer.customerType as CustomerType) || '',
      billingAddress: customer.billingAddress || '',
      city: customer.city || '',
      country: customer.country || '',
      industry: customer.industry || '',
      website: customer.website || '',
      notes: customer.notes || '',
    })
    setShowCustomerDialog(true)
  }

  const handleSaveCustomer = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentAccount || !currentProject || !customerForm.customerName) return

    setIsSaving(true)
    try {
      const data = {
        customerName: customerForm.customerName,
        customerType: customerForm.customerType || null,
        billingAddress: customerForm.billingAddress || null,
        city: customerForm.city || null,
        country: customerForm.country || null,
        industry: customerForm.industry || null,
        website: customerForm.website || null,
        notes: customerForm.notes || null,
      }

      if (editingCustomer) {
        const updated = await customersApi.update(currentAccount.id, currentProject.id, editingCustomer.id, data)
        setCustomers(prev => prev.map(c => c.id === updated.id ? updated : c))
      } else {
        const created = await customersApi.create(currentAccount.id, currentProject.id, data)
        setCustomers(prev => [...prev, created])
      }
      setShowCustomerDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save customer')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteCustomer = async () => {
    if (!currentAccount || !currentProject || !deleteCustomerId) return
    setIsSaving(true)
    try {
      await customersApi.delete(currentAccount.id, currentProject.id, deleteCustomerId)
      setCustomers(prev => prev.filter(c => c.id !== deleteCustomerId))
      setDeleteCustomerId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete customer')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreatePerson = () => {
    setEditingPerson(null)
    setPersonForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      notes: '',
    })
    setShowPersonDialog(true)
  }

  const handleEditPerson = (person: Person) => {
    setEditingPerson(person)
    setPersonForm({
      firstName: person.firstName,
      lastName: person.lastName,
      email: person.email || '',
      phone: person.phone || '',
      jobTitle: person.jobTitle || '',
      notes: person.notes || '',
    })
    setShowPersonDialog(true)
  }

  const handleSavePerson = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentAccount || !currentProject || !personForm.firstName || !personForm.lastName) return

    setIsSaving(true)
    try {
      const data = {
        firstName: personForm.firstName,
        lastName: personForm.lastName,
        email: personForm.email || null,
        phone: personForm.phone || null,
        jobTitle: personForm.jobTitle || null,
        notes: personForm.notes || null,
      }

      if (editingPerson) {
        const updated = await personsApi.update(currentAccount.id, currentProject.id, editingPerson.id, data)
        setPersons(prev => prev.map(p => p.id === updated.id ? updated : p))
      } else {
        const created = await personsApi.create(currentAccount.id, currentProject.id, data)
        setPersons(prev => [...prev, created])
      }
      setShowPersonDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save contact')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeletePerson = async () => {
    if (!currentAccount || !currentProject || !deletePersonId) return
    setIsSaving(true)
    try {
      await personsApi.delete(currentAccount.id, currentProject.id, deletePersonId)
      setPersons(prev => prev.filter(p => p.id !== deletePersonId))
      setDeletePersonId(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete contact')
    } finally {
      setIsSaving(false)
    }
  }

  // Contact management handlers
  const handleManageContacts = async (customer: Customer) => {
    if (!currentAccount || !currentProject) return
    setSelectedCustomer(customer)
    setShowContactsDialog(true)
    setIsLoadingContacts(true)
    try {
      const result = await customersApi.get(currentAccount.id, currentProject.id, customer.id)
      setCustomerContacts(result.contacts)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load contacts')
    } finally {
      setIsLoadingContacts(false)
    }
  }

  const handleAddContactToCustomer = async (personId: string, role?: string) => {
    if (!currentAccount || !currentProject || !selectedCustomer) return
    setIsSaving(true)
    try {
      await customersApi.addContact(currentAccount.id, currentProject.id, selectedCustomer.id, {
        personId,
        role: role as 'primary' | 'billing' | 'technical' | 'decision_maker' | undefined,
      })
      // Reload contacts
      const result = await customersApi.get(currentAccount.id, currentProject.id, selectedCustomer.id)
      setCustomerContacts(result.contacts)
      setShowAddContactDialog(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add contact')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveContactFromCustomer = async (personId: string) => {
    if (!currentAccount || !currentProject || !selectedCustomer) return
    setIsSaving(true)
    try {
      await customersApi.removeContact(currentAccount.id, currentProject.id, selectedCustomer.id, personId)
      setCustomerContacts(prev => prev.filter(c => c.person.id !== personId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove contact')
    } finally {
      setIsSaving(false)
    }
  }

  // Get contacts not yet linked to the selected customer
  const availableContacts = persons.filter(
    p => !customerContacts.some(cc => cc.person.id === p.id)
  )

  if (!currentProject) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-text-muted">Please select a project first</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-solution animate-spin" />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {error && (
        <div className="mb-4 p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm">
          {error}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="customers" className="gap-2">
              <Building2 className="w-4 h-4" />
              Customers ({customers.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="gap-2">
              <User className="w-4 h-4" />
              Contacts ({persons.length})
            </TabsTrigger>
          </TabsList>

          {activeTab === 'customers' ? (
            <Button onClick={handleCreateCustomer}>
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          ) : (
            <Button onClick={handleCreatePerson}>
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          )}
        </div>

        <TabsContent value="customers" className="flex-1 overflow-auto mt-0">
          {customers.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Building2 className="w-12 h-12 text-text-muted mb-4" />
              <h3 className="font-medium text-text mb-1">No customers yet</h3>
              <p className="text-text-muted text-sm mb-4">
                Add your first customer to start tracking your relationships
              </p>
              <Button onClick={handleCreateCustomer}>
                <Plus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {customers.map(customer => (
                <Card key={customer.id} className="group relative">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">{customer.customerName}</CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleManageContacts(customer)}>
                            <Users className="mr-2 h-4 w-4" />
                            Manage Contacts
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleEditCustomer(customer)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-error focus:text-error"
                            onClick={() => setDeleteCustomerId(customer.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {customer.customerType && (
                      <Badge
                        variant="outline"
                        className={customerTypeColors[customer.customerType as CustomerType]}
                      >
                        {customer.customerType}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {customer.industry && (
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Briefcase className="w-4 h-4" />
                        {customer.industry}
                      </div>
                    )}
                    {(customer.city || customer.country) && (
                      <div className="flex items-center gap-2 text-text-secondary">
                        <MapPin className="w-4 h-4" />
                        {[customer.city, customer.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {customer.website && (
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Globe className="w-4 h-4" />
                        <a
                          href={customer.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-solution truncate"
                        >
                          {customer.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="contacts" className="flex-1 overflow-auto mt-0">
          {persons.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <User className="w-12 h-12 text-text-muted mb-4" />
              <h3 className="font-medium text-text mb-1">No contacts yet</h3>
              <p className="text-text-muted text-sm mb-4">
                Add your first contact to start building your network
              </p>
              <Button onClick={handleCreatePerson}>
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {persons.map(person => (
                <Card key={person.id} className="group relative">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base">
                        {person.firstName} {person.lastName}
                      </CardTitle>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditPerson(person)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-error focus:text-error"
                            onClick={() => setDeletePersonId(person.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    {person.jobTitle && (
                      <p className="text-sm text-text-secondary">{person.jobTitle}</p>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    {person.email && (
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Mail className="w-4 h-4" />
                        <a href={`mailto:${person.email}`} className="hover:text-solution truncate">
                          {person.email}
                        </a>
                      </div>
                    )}
                    {person.phone && (
                      <div className="flex items-center gap-2 text-text-secondary">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${person.phone}`} className="hover:text-solution">
                          {person.phone}
                        </a>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Customer Dialog */}
      <Dialog open={showCustomerDialog} onOpenChange={setShowCustomerDialog}>
        <DialogContent>
          <DialogHeader className="pb-4 border-b border-warm-200">
            <DialogTitle className="text-xl font-semibold text-text">
              {editingCustomer ? 'Edit Customer' : 'New Customer'}
            </DialogTitle>
            <p className="text-sm text-text-muted mt-1">
              {editingCustomer ? 'Update customer information' : 'Add a new customer to your CRM'}
            </p>
          </DialogHeader>
          <form onSubmit={handleSaveCustomer}>
            <DialogBody className="space-y-5 max-h-[55vh] py-4">
              <div>
                <label className="block text-sm font-medium text-text mb-2">
                  Customer Name <span className="text-error">*</span>
                </label>
                <input
                  type="text"
                  value={customerForm.customerName}
                  onChange={e => setCustomerForm(f => ({ ...f, customerName: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  placeholder="Enter customer name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Type</label>
                  <select
                    value={customerForm.customerType}
                    onChange={e => setCustomerForm(f => ({ ...f, customerType: e.target.value as CustomerType | '' }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  >
                    <option value="">Select type</option>
                    <option value="prospect">Prospect</option>
                    <option value="active">Active</option>
                    <option value="churned">Churned</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Industry</label>
                  <input
                    type="text"
                    value={customerForm.industry}
                    onChange={e => setCustomerForm(f => ({ ...f, industry: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="e.g. Technology"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Address</label>
                <input
                  type="text"
                  value={customerForm.billingAddress}
                  onChange={e => setCustomerForm(f => ({ ...f, billingAddress: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">City</label>
                  <input
                    type="text"
                    value={customerForm.city}
                    onChange={e => setCustomerForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Country</label>
                  <input
                    type="text"
                    value={customerForm.country}
                    onChange={e => setCustomerForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="Country"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Website</label>
                <input
                  type="url"
                  value={customerForm.website}
                  onChange={e => setCustomerForm(f => ({ ...f, website: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  placeholder="https://example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Notes</label>
                <textarea
                  value={customerForm.notes}
                  onChange={e => setCustomerForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-3 pt-4 border-t border-warm-200">
              <Button type="button" variant="outline" onClick={() => setShowCustomerDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingCustomer ? 'Update Customer' : 'Add Customer'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Person Dialog */}
      <Dialog open={showPersonDialog} onOpenChange={setShowPersonDialog}>
        <DialogContent>
          <DialogHeader className="pb-4 border-b border-warm-200">
            <DialogTitle className="text-xl font-semibold text-text">
              {editingPerson ? 'Edit Contact' : 'New Contact'}
            </DialogTitle>
            <p className="text-sm text-text-muted mt-1">
              {editingPerson ? 'Update contact information' : 'Add a new contact to your network'}
            </p>
          </DialogHeader>
          <form onSubmit={handleSavePerson}>
            <DialogBody className="space-y-5 max-h-[55vh] py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    First Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={personForm.firstName}
                    onChange={e => setPersonForm(f => ({ ...f, firstName: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="First name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">
                    Last Name <span className="text-error">*</span>
                  </label>
                  <input
                    type="text"
                    value={personForm.lastName}
                    onChange={e => setPersonForm(f => ({ ...f, lastName: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Job Title</label>
                <input
                  type="text"
                  value={personForm.jobTitle}
                  onChange={e => setPersonForm(f => ({ ...f, jobTitle: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                  placeholder="e.g. Product Manager"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Email</label>
                  <input
                    type="email"
                    value={personForm.email}
                    onChange={e => setPersonForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Phone</label>
                  <input
                    type="tel"
                    value={personForm.phone}
                    onChange={e => setPersonForm(f => ({ ...f, phone: e.target.value }))}
                    className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors"
                    placeholder="+1 234 567 890"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-text mb-2">Notes</label>
                <textarea
                  value={personForm.notes}
                  onChange={e => setPersonForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border border-warm-300 rounded-lg text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors resize-none"
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </DialogBody>
            <div className="flex justify-end gap-3 pt-4 border-t border-warm-200">
              <Button type="button" variant="outline" onClick={() => setShowPersonDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  editingPerson ? 'Update Contact' : 'Add Contact'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Customer Confirmation */}
      <AlertDialog open={!!deleteCustomerId} onOpenChange={(open) => !open && setDeleteCustomerId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this customer? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCustomer}
              disabled={isSaving}
              className="bg-error hover:bg-error/90"
            >
              {isSaving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Person Confirmation */}
      <AlertDialog open={!!deletePersonId} onOpenChange={(open) => !open && setDeletePersonId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this contact? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePerson}
              disabled={isSaving}
              className="bg-error hover:bg-error/90"
            >
              {isSaving ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage Customer Contacts Dialog */}
      <Dialog open={showContactsDialog} onOpenChange={(open) => {
        if (!open) {
          setShowContactsDialog(false)
          setSelectedCustomer(null)
          setCustomerContacts([])
        }
      }}>
        <DialogContent>
          <DialogHeader className="pb-4 border-b border-warm-200">
            <DialogTitle className="text-xl font-semibold text-text">
              Contacts for {selectedCustomer?.customerName}
            </DialogTitle>
            <p className="text-sm text-text-muted mt-1">
              Manage contacts linked to this customer
            </p>
          </DialogHeader>
          <DialogBody className="py-4">
            {isLoadingContacts ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-solution animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">
                    {customerContacts.length} contact{customerContacts.length !== 1 ? 's' : ''} linked
                  </span>
                  <Button
                    size="sm"
                    onClick={() => setShowAddContactDialog(true)}
                    disabled={availableContacts.length === 0}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Contact
                  </Button>
                </div>

                {customerContacts.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 text-text-muted mx-auto mb-3" />
                    <p className="text-text-muted text-sm">No contacts linked yet</p>
                    <p className="text-text-muted text-xs mt-1">
                      Click "Add Contact" to link contacts to this customer
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {customerContacts.map(({ person, role, isPrimary }) => (
                      <div
                        key={person.id}
                        className="flex items-center justify-between p-3 bg-warm-50 rounded-lg border border-warm-200"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-solution/10 flex items-center justify-center">
                            <User className="w-4 h-4 text-solution" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-text">
                                {person.firstName} {person.lastName}
                              </span>
                              {isPrimary && (
                                <Badge variant="outline" className="text-xs bg-solution/10 text-solution border-solution/20">
                                  Primary
                                </Badge>
                              )}
                              {role && (
                                <Badge variant="outline" className="text-xs capitalize">
                                  {role.replace('_', ' ')}
                                </Badge>
                              )}
                            </div>
                            {(person.email || person.jobTitle) && (
                              <p className="text-xs text-text-muted">
                                {person.jobTitle}{person.jobTitle && person.email ? ' • ' : ''}{person.email}
                              </p>
                            )}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveContactFromCustomer(person.id)}
                          disabled={isSaving}
                          className="text-text-muted hover:text-error"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </DialogBody>
          <div className="flex justify-end pt-4 border-t border-warm-200">
            <Button variant="outline" onClick={() => setShowContactsDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Contact to Customer Dialog */}
      <Dialog open={showAddContactDialog} onOpenChange={setShowAddContactDialog}>
        <DialogContent>
          <DialogHeader className="pb-4 border-b border-warm-200">
            <DialogTitle className="text-xl font-semibold text-text">
              Add Contact
            </DialogTitle>
            <p className="text-sm text-text-muted mt-1">
              Select a contact to link to {selectedCustomer?.customerName}
            </p>
          </DialogHeader>
          <DialogBody className="py-4">
            {availableContacts.length === 0 ? (
              <div className="text-center py-8">
                <User className="w-10 h-10 text-text-muted mx-auto mb-3" />
                <p className="text-text-muted text-sm">No available contacts</p>
                <p className="text-text-muted text-xs mt-1">
                  All contacts are already linked or you need to create new contacts first
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {availableContacts.map(person => (
                  <div
                    key={person.id}
                    className="flex items-center justify-between p-3 bg-warm-50 rounded-lg border border-warm-200 hover:border-solution/30 transition-colors cursor-pointer"
                    onClick={() => handleAddContactToCustomer(person.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-warm-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-text-secondary" />
                      </div>
                      <div>
                        <span className="font-medium text-text">
                          {person.firstName} {person.lastName}
                        </span>
                        {(person.email || person.jobTitle) && (
                          <p className="text-xs text-text-muted">
                            {person.jobTitle}{person.jobTitle && person.email ? ' • ' : ''}{person.email}
                          </p>
                        )}
                      </div>
                    </div>
                    <UserPlus className="w-4 h-4 text-text-muted" />
                  </div>
                ))}
              </div>
            )}
          </DialogBody>
          <div className="flex justify-end pt-4 border-t border-warm-200">
            <Button variant="outline" onClick={() => setShowAddContactDialog(false)}>
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
